/* =========================================================================
   Local Feast Days badge store ('catholics.feasts'). Earning a feast badge is
   fully guest/offline-safe here; accounts additionally grant server-side via
   api.grantFeastBadge (the Feast Days CollectionItem, db/018 → db/016).

   Feast key derivation is a CROSS-AGENT contract: the CollectionItem key is
   'feast_<FeastDayId>' (db/016 §3 line 84 — `N'feast_' + CAST(f.FeastDayId ...)`),
   and usp_FeastBadge_Grant(@FeastKey) expects exactly that string (db/018 §4).
   feastKey() is the single place that derivation lives client-side.

   House pattern: pure helpers over an injectable KVStore seam (mirrors
   dailyBlessing.ts / inventory.ts) so the pure logic is unit-testable.
   ========================================================================= */
import type { KVStore } from './dailyBlessing';

const KEY = 'catholics.feasts';

/** The CollectionItem / badge key for a feast day. MUST match db/016 §3. */
export function feastKey(feastDayId: number): string {
  return 'feast_' + feastDayId;
}

/** One locally-earned feast badge. `date` is the ET calendar date it was visited
    (YYYY-MM-DD), used to place the badge on the collections calendar view. */
export interface FeastBadge {
  earnedUtc: string;
  title: string;
  date: string;
}

export type FeastBadgeMap = Record<string, FeastBadge>;

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readFeasts(store: KVStore | null = deviceStore()): FeastBadgeMap {
  try {
    if (!store) return {};
    const raw = store.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as FeastBadgeMap) : {};
  } catch {
    return {};
  }
}

function writeFeasts(map: FeastBadgeMap, store: KVStore | null): void {
  try {
    if (store) store.setItem(KEY, JSON.stringify(map));
  } catch {
    /* private mode — badge simply won't persist */
  }
}

/** Has the feast for this id already been earned locally? */
export function hasFeastBadge(feastDayId: number, store: KVStore | null = deviceStore()): boolean {
  return !!readFeasts(store)[feastKey(feastDayId)];
}

/** Earn a feast badge locally (idempotent per feast key). Returns true only on
    the FIRST earn so the caller fires telemetry / grants exactly once. */
export function earnFeastBadge(
  feastDayId: number,
  title: string,
  date: string,
  now: Date = new Date(),
  store: KVStore | null = deviceStore(),
): boolean {
  const key = feastKey(feastDayId);
  const map = readFeasts(store);
  if (map[key]) return false;
  map[key] = { earnedUtc: now.toISOString(), title, date };
  writeFeasts(map, store);
  return true;
}
